import React, { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  Connection,
  Edge,
  Node,
  ReactFlowProvider,
  ReactFlowInstance,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { NoteType, NoteMode, AnalysisResults } from '../types/notes';
import { useFeatureFlags, useFeatureFlag } from '@/hooks/useFeatureFlags';
import { analyzeNotes } from '@/services/openai';
import { useToast } from '@/components/ui/use-toast';
import { useUsageTracking } from '@/hooks/useUsageTracking';
import { supabase } from '@/integrations/supabase/client';
import { Navbar } from '../components/Navbar';
import { Toolbar } from '../components/Toolbar';
import { ModeToggle } from '../components/ModeToggle';
import { AnalysisPanel } from '../components/AnalysisPanel';
import { StickyNote } from '../components/StickyNote';
import { ClusterNode } from '../components/ClusterNode';
import posthog from 'posthog-js';

import { QuestionSetup } from '../components/QuestionSetup';
import { GuidancePanel } from '../components/GuidancePanel';
import { HighlightsPanel } from '../components/HighlightsPanel';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { QuestionNode } from '@/components/QuestionDisplay';
import { useSupabaseNoteSpace } from '@/hooks/useSupabaseNoteSpace';
import { SharingPanel } from '@/components/SharingPanel';
import { useCollaboration } from '@/hooks/useCollaboration';
import { flowNodeService } from '@/services/supabase';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { MobileAnalysisPanel, MobileGuidancePanel, MobileHighlightsPanel, MobileSharingPanel } from '@/components/mobile';

// Define node types for React Flow
const nodeTypes = {
  note: StickyNote,
  cluster: ClusterNode,
  question: QuestionNode,
};

const NoteSpace = () => {
  const { spaceId } = useParams<{ spaceId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  console.log('NoteSpace component rendered with:', { 
    spaceId, 
    user: !!user, 
    authLoading,
    pathname: window.location.pathname 
  });

  // Early return for auth loading
  if (authLoading) {
    console.log('Auth still loading, showing spinner');
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-lg">Authenticating...</div>
        </div>
      </div>
    );
  }

  // Check authentication after loading is complete
  if (!user) {
    console.log('No user found, redirecting to auth');
    navigate('/auth');
    return null;
  }

  // Check for spaceId
  if (!spaceId) {
    console.log('No spaceId found, redirecting to dashboard');
    navigate('/');
    return null;
  }

  return <NoteSpaceContent spaceId={spaceId} />;
};

// Separate component to handle the actual note space functionality
const NoteSpaceContent = ({ spaceId }: { spaceId: string }) => {
  const isMobile = useIsMobile();
  
  const [reactFlowNodes, setReactFlowNodes, onNodesChange] = useNodesState([]);
  const [reactFlowEdges, setReactFlowEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  
  const {
    nodes,
    edges,
    sessionName,
    questions,
    loading,
    error,
    isOwner,
    userPermission,
    handleAddNote,
    handleSaveNote: originalHandleSaveNote,
    handleDeleteNote,
    handleEditNote,
    handleHighlightNote,
    handleChangeNoteType,
    updateSessionName,
    saveQuestions,
    addQuestions
  } = useSupabaseNoteSpace(spaceId, reactFlowNodes);
  
  const [showQuestionSetup, setShowQuestionSetup] = useState(false);

  // Show question setup when there are no questions saved in database and no notes
  React.useEffect(() => {
    const hasExistingQuestions = questions.length > 0;
    const shouldShow = !hasExistingQuestions && nodes.length === 0 && !loading;
    setShowQuestionSetup(shouldShow);
  }, [questions.length, nodes.length, loading]);

  // Load questions from database and localStorage as fallback
  const [big3Questions, backupQuestions] = React.useMemo(() => {
    // First try to get questions from database
    if (questions.length > 0) {
      const big3 = questions.filter(q => q.category === 'big3').slice(0, 3);
      const backup = questions.filter(q => q.category === 'backup');
      return [big3, backup];
    }
    
    // Fallback to localStorage for backwards compatibility
    const savedBig3 = localStorage.getItem(`yakstack_big3_${spaceId}`);
    const savedBackup = localStorage.getItem(`yakstack_backup_${spaceId}`);
    
    const big3 = savedBig3 ? JSON.parse(savedBig3) : [];
    const backup = savedBackup ? JSON.parse(savedBackup) : [];
    
    return [big3, backup];
  }, [questions, spaceId]);
  const [isAnalysisPanelOpen, setIsAnalysisPanelOpen] = useState(false);
  const [isGuidancePanelOpen, setIsGuidancePanelOpen] = useState(false);
  const [isBackupQuestionsOpen, setIsBackupQuestionsOpen] = useState(false);
  const [isGrouped, setIsGrouped] = useState(false);
  const [isTimelineSorted, setIsTimelineSorted] = useState(false);
  const [originalPositions, setOriginalPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [isSharingPanelOpen, setIsSharingPanelOpen] = useState(false);
  const [isHighlightsPanelOpen, setIsHighlightsPanelOpen] = useState(false);
  const [currentMode, setCurrentMode] = useState<NoteMode>('capturing');
  const [isAiOrganizing, setIsAiOrganizing] = useState(false);
  
  // AI Feature toggles
  const [aiAnalysisEnabled, setAiAnalysisEnabled] = useState(true);
  const liveGuidanceEnabled = useFeatureFlag('enableLiveGuidance'); // PostHog controlled
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const { toast } = useToast();
  const { checkCanRunAnalysis } = useUsageTracking();

  // Create a wrapper for handleSaveNote that triggers guidance updates
  const handleSaveNote = useCallback(async (nodeId: string, content: string) => {
    const onNoteSaved = isGuidancePanelOpen && liveGuidanceEnabled ? () => {
      // Trigger guidance refresh by forcing re-render of guidance components
      setReactFlowNodes(prev => [...prev]);
    } : undefined;
    
    await originalHandleSaveNote(nodeId, content, onNoteSaved);
  }, [originalHandleSaveNote, isGuidancePanelOpen, liveGuidanceEnabled, setReactFlowNodes]);

  useCollaboration({
    spaceId,
    nodes: reactFlowNodes,
    edges: reactFlowEdges,
    setNodes: setReactFlowNodes,
    setEdges: setReactFlowEdges,
    onSaveNote: handleSaveNote,
    onDeleteNote: handleDeleteNote,
    onEditNote: handleEditNote,
    onHighlightNote: handleHighlightNote
  });

  React.useEffect(() => {    
    // Smart sync that preserves React Flow positions and adds question nodes
    setReactFlowNodes(prevReactFlowNodes => {
      const prevNodeMap = new Map(prevReactFlowNodes.map(node => [node.id, node]));
      
      // Create nodes from database notes (filter out any existing question nodes)
      const noteNodes = nodes
        .filter(dbNode => dbNode.type !== 'question') // Remove any leftover question nodes
        .map(dbNode => {
          const existingReactFlowNode = prevNodeMap.get(dbNode.id);
          
          if (existingReactFlowNode) {
            // Preserve React Flow position and only update data
            return {
              ...dbNode,
              position: existingReactFlowNode.position, // Keep React Flow position
              data: dbNode.data // Update data from database
            };
          } else {
            // New node, use database position
            return dbNode;
          }
        });

      // Create question nodes for Big 3 (always visible on canvas)
      const questionNodes = big3Questions.map((question, index) => {
        const questionNodeId = `question-${index}`;
        const existingQuestionNode = prevNodeMap.get(questionNodeId);
        
        return {
          id: questionNodeId,
          type: 'question',
          position: existingQuestionNode?.position || { x: 50 + (index * 350), y: 50 },
          data: {
            id: questionNodeId,
            text: question.text,
            type: 'open' as const,
            index: index,
            isBig3: true
          },
          draggable: true,
          selectable: true
        };
      });

      // Create toggle arrow node if there are backup questions
      const toggleNodes = backupQuestions.length > 0 ? [{
        id: 'backup-toggle',
        type: 'question',
        position: { x: 400, y: 180 },
        data: {
          id: 'backup-toggle',
          text: isBackupQuestionsOpen ? 'Hide backup questions' : 'Show backup questions',
          type: 'toggle' as const,
          isToggle: true,
          isOpen: isBackupQuestionsOpen,
          onClick: () => setIsBackupQuestionsOpen(!isBackupQuestionsOpen)
        },
        draggable: true,
        selectable: true
      }] : [];

      // Create backup question nodes (only if expanded)
      const backupQuestionNodes = isBackupQuestionsOpen ? backupQuestions.map((question, index) => {
        const questionNodeId = `backup-question-${index}`;
        const existingQuestionNode = prevNodeMap.get(questionNodeId);
        
        return {
          id: questionNodeId,
          type: 'question',
          position: existingQuestionNode?.position || { x: 50 + (index * 300), y: 250 },
          data: {
            id: questionNodeId,
            text: question.text,
            type: 'open' as const,
            index: index,
            isBackup: true
          },
          draggable: true,
          selectable: true
        };
      }) : [];

      return [...noteNodes, ...questionNodes, ...toggleNodes, ...backupQuestionNodes];
    });
  }, [nodes, questions, isBackupQuestionsOpen, setReactFlowNodes, big3Questions, backupQuestions]);

  React.useEffect(() => {
    setReactFlowEdges(edges);
  }, [edges, setReactFlowEdges]);

  const getStickyNoteData = (data: Record<string, unknown>) => {
    return {
      type: (data.type as NoteType) || 'note',
      content: (data.content as string) || '',
      isEditing: (data.isEditing as boolean) || false,
      highlighted: (data.highlighted as boolean) || false,
      onSave: data.onSave as ((nodeId: string, content: string) => void) | undefined,
      onDelete: data.onDelete as ((nodeId: string) => void) | undefined,
      onEdit: data.onEdit as ((nodeId: string, isEditing: boolean) => void) | undefined,
      onHighlight: data.onHighlight as ((nodeId: string) => void) | undefined,
      onChangeType: data.onChangeType as ((nodeId: string, newType: NoteType) => void) | undefined,
    };
  };

  const noteTypeCounts = useMemo(() => {
    const counts: Record<NoteType, number> = {
      note: 0,
      quote: 0,
      emotion: 0,
      behaviour: 0,
      pain: 0,
      insight: 0,
      opportunity: 0
    };
    
    reactFlowNodes.forEach(node => {
      if (node.data && node.data.type) {
        const noteData = getStickyNoteData(node.data);
        counts[noteData.type]++;
      }
    });
    
    return counts;
  }, [reactFlowNodes]);

  const onConnect = useCallback(
    (params: Edge | Connection) => setReactFlowEdges((eds) => addEdge(params, eds)),
    [setReactFlowEdges]
  );

  const onNodeDragStop = useCallback(async (event: any, node: Node) => {
    if (userPermission === 'view') return;
    
    try {
      // Save the new position to the database
      await flowNodeService.update(node.id, {
        position_x: node.position.x,
        position_y: node.position.y
      });
    } catch (error) {
      console.error('Failed to save node position:', error);
    }
  }, [userPermission]);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      const data = JSON.parse(event.dataTransfer.getData('application/json') || '{}');

      if (typeof type === 'undefined' || !type || !reactFlowInstance) {
        return;
      }

      if (type === 'stickyNote') {
        handleAddNote(data.noteType || 'note', reactFlowInstance);
      }
    },
    [reactFlowInstance, handleAddNote]
  );

  const handleAnalyze = async () => {
    if (!aiAnalysisEnabled) return;
    
    // Check usage limits before running analysis
    const { canRun, currentUsage, limit } = await checkCanRunAnalysis();
    if (!canRun) {
      toast({
        title: "Monthly Analysis Limit Reached",
        description: `You've used ${currentUsage}/${limit} analyses this month. Limit resets next month.`,
        variant: "destructive",
      });
      return;
    }
    
    setIsAnalyzing(true);
    setIsAnalysisPanelOpen(true);
    
    try {
      // Get all notes from current note space
      const notesData = reactFlowNodes
        .filter(node => node.data && node.data.content)
        .map(node => ({
          type: node.data.type || 'note',
          content: node.data.content
        }));
      
      if (notesData.length === 0) {
        setIsAnalyzing(false);
        setIsAnalysisPanelOpen(false);
        toast({
          title: "No notes to analyze",
          description: "Add some notes to your canvas before running analysis.",
          variant: "destructive",
        });
        return;
      }
      
      // Extract all questions for analysis context
      const allQuestions = [
        ...big3Questions.map(q => q.text),
        ...backupQuestions.map(q => q.text)
      ];
      
      const analysis = await analyzeNotes(notesData, allQuestions, spaceId ? [spaceId] : undefined);
      setAnalysisResults(analysis as AnalysisResults);
      
      toast({
        title: "Analysis complete",
        description: "AI insights have been generated from your notes.",
      });
    } catch (error) {
      console.error('Analysis failed:', error);
      toast({
        title: "Analysis failed",
        description: "Failed to analyze notes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleCloseAnalysisPanel = () => {
    setIsAnalysisPanelOpen(false);
  };

  const handleQuestionSetupComplete = (big3Questions: string[], backupQuestions: string[]) => {
    console.log('Questions setup completed:', { big3Questions, backupQuestions });
    
    // Add all questions (both big3 and backup) to the workspace
    const allQuestions = [...big3Questions, ...backupQuestions];
    if (allQuestions.length > 0) {
      addQuestions(allQuestions);
    }
    
    setShowQuestionSetup(false);
  };

  const handleOpenGuidance = () => {
    if (!liveGuidanceEnabled) return;
    setIsGuidancePanelOpen(true);
  };

  const handleCloseGuidance = () => {
    setIsGuidancePanelOpen(false);
  };

  const handleModeChange = (mode: NoteMode) => {
    setCurrentMode(mode);
  };

  const handleQuickNote = useCallback(() => {
    handleAddNote('note', reactFlowInstance);
  }, [handleAddNote, reactFlowInstance]);

  const handleOpenHighlights = () => {
    setIsHighlightsPanelOpen(true);
  };

  const handleCloseHighlights = () => {
    setIsHighlightsPanelOpen(false);
  };

  const handleGroupByType = () => {
    // Store original positions if not already stored
    if (!isGrouped && !isTimelineSorted && Object.keys(originalPositions).length === 0) {
      const positions: Record<string, { x: number; y: number }> = {};
      reactFlowNodes.forEach(node => {
        positions[node.id] = { ...node.position };
      });
      setOriginalPositions(positions);
    }

    const typePositions: Record<NoteType, { x: number; y: number }> = {
      note: { x: 100, y: 100 },
      quote: { x: 300, y: 100 },
      emotion: { x: 500, y: 100 },
      behaviour: { x: 700, y: 100 },
      pain: { x: 900, y: 100 },
      insight: { x: 1100, y: 100 },
      opportunity: { x: 1300, y: 100 }
    };

    const typeCounts: Record<NoteType, number> = {
      note: 0,
      quote: 0,
      emotion: 0,
      behaviour: 0,
      pain: 0,
      insight: 0,
      opportunity: 0
    };

    setReactFlowNodes((nds) =>
      nds.map((node) => {
        if (node.data && node.data.type) {
          const noteData = getStickyNoteData(node.data);
          const basePosition = typePositions[noteData.type];
          
          // Skip nodes with invalid types
          if (!basePosition) {
            console.warn(`Unknown note type: ${noteData.type}`);
            return node;
          }
          
          const offset = typeCounts[noteData.type] * 150;
          typeCounts[noteData.type]++;

          return {
            ...node,
            position: {
              x: basePosition.x,
              y: basePosition.y + offset
            }
          };
        }
        return node;
      })
    );

    setIsGrouped(true);
    setIsTimelineSorted(false); // Reset timeline sort when grouping
  };

  const handleUngroup = () => {
    if (Object.keys(originalPositions).length > 0) {
      // Restore original positions
      setReactFlowNodes((nds) =>
        nds.map((node) => {
          const originalPos = originalPositions[node.id];
          const currentPos = node.position;
          
          return {
            ...node,
            position: originalPos || currentPos || { x: Math.random() * 800 + 100, y: Math.random() * 600 + 100 }
          };
        })
      );
    } else {
      // Fallback to random positions
      setReactFlowNodes((nds) =>
        nds.map((node) => ({
          ...node,
          position: node.position || {
            x: Math.random() * 800 + 100,
            y: Math.random() * 600 + 100
          }
        }))
      );
    }

    setIsGrouped(false);
    setIsTimelineSorted(false);
    setOriginalPositions({}); // Clear stored positions
  };

  const handleSortByTimeline = (order: 'newest' | 'oldest') => {
    // Store original positions if not already stored
    if (!isTimelineSorted && !isGrouped && Object.keys(originalPositions).length === 0) {
      const positions: Record<string, { x: number; y: number }> = {};
      reactFlowNodes.forEach(node => {
        positions[node.id] = { ...node.position };
      });
      setOriginalPositions(positions);
    }

    // Get nodes with timestamps and sort them
    const nodesWithTimestamps = reactFlowNodes
      .filter(node => node.data && node.data.type) // Only sticky notes
      .map(node => {
        // Extract timestamp from node creation or use current position as fallback
        const timestamp = new Date((node as any).created_at || Date.now()).getTime();
        return { node, timestamp };
      })
      .sort((a, b) => {
        return order === 'newest' ? b.timestamp - a.timestamp : a.timestamp - b.timestamp;
      });

    // Arrange nodes in a vertical timeline
    const startX = 200;
    const startY = 100;
    const verticalSpacing = 180;

    setReactFlowNodes((nds) =>
      nds.map((node) => {
        const sortedIndex = nodesWithTimestamps.findIndex(item => item.node.id === node.id);
        
        if (sortedIndex !== -1) {
          return {
            ...node,
            position: {
              x: startX,
              y: startY + (sortedIndex * verticalSpacing)
            }
          };
        }
        return node;
      })
    );

    setIsTimelineSorted(true);
    setIsGrouped(false); // Reset grouping when sorting by timeline
  };


  const handleTimelineToggle = () => {
    if (isTimelineSorted) {
      handleUngroup(); // This will restore original positions
    } else {
      handleSortByTimeline('newest');
    }
  };

  const handleAiOrganize = async () => {
    if (!reactFlowNodes.some(node => node.data && node.data.content)) {
      toast({
        title: "No notes to organize",
        description: "Add some notes to your canvas before organizing.",
        variant: "destructive",
      });
      return;
    }

    setIsAiOrganizing(true);
    let startTime = Date.now();
    
    try {
      // Get canvas dimensions from React Flow viewport
      const viewport = reactFlowInstance?.getViewport();
      const canvasWidth = Math.max(1200, viewport?.x ? Math.abs(viewport.x) + 1200 : 1200);
      const canvasHeight = Math.max(800, viewport?.y ? Math.abs(viewport.y) + 800 : 800);

      // Prepare notes data for AI organization
      const notesForAI = reactFlowNodes
        .filter(node => node.data && node.data.content && node.data.type)
        .map(node => ({
          id: node.id,
          type: node.data.type,
          content: node.data.content,
          created_at: (node as any).created_at || new Date().toISOString(),
          position: node.position
        }));

      console.log('Sending notes to AI organization:', { 
        count: notesForAI.length, 
        canvasWidth, 
        canvasHeight 
      });

      // Track LLM call start
      startTime = Date.now();
      posthog.capture('llm_call_started', {
        model: 'gpt-4.1-2025-04-14',
        provider: 'openai',
        input_length: notesForAI.length,
        type: 'organize_notes',
        canvas_width: canvasWidth,
        canvas_height: canvasHeight,
        start_time: startTime
      });

      // Call the AI organization edge function
      const { data, error } = await supabase.functions.invoke('ai-organize-notes', {
        body: {
          notes: notesForAI,
          canvasWidth,
          canvasHeight
        }
      });

      if (error) {
        console.error('AI organization error:', error);
        throw new Error(error.message || 'AI organization failed');
      }

      if (!data.positions) {
        throw new Error('Invalid response from AI organization service');
      }

      console.log('AI organization successful:', data.positions.length, 'positions returned');

      // Track LLM call completion
      const endTime = Date.now();
      posthog.capture('llm_call_completed', {
        model: 'gpt-4.1-2025-04-14',
        provider: 'openai',
        input_length: notesForAI.length,
        output_size: data.positions.length,
        start_time: startTime,
        end_time: endTime,
        response_time: endTime - startTime,
        type: 'organize_notes',
        canvas_width: canvasWidth,
        canvas_height: canvasHeight,
        success: true,
        positions_generated: data.positions.length
      });

      // Store original positions before organizing
      if (Object.keys(originalPositions).length === 0) {
        const positions: Record<string, { x: number; y: number }> = {};
        reactFlowNodes.forEach(node => {
          positions[node.id] = { ...node.position };
        });
        setOriginalPositions(positions);
      }

      // Apply the AI-organized positions
      setReactFlowNodes(prevNodes =>
        prevNodes.map(node => {
          const aiPosition = data.positions.find((pos: any) => pos.id === node.id);
          if (aiPosition) {
            return {
              ...node,
              position: aiPosition.position
            };
          }
          return node;
        })
      );

      // Update database with new positions
      for (const pos of data.positions) {
        try {
          await flowNodeService.update(pos.id, {
            position_x: pos.position.x,
            position_y: pos.position.y
          });
        } catch (updateError) {
          console.warn('Failed to save position for node:', pos.id, updateError);
        }
      }

      // Reset other organization states
      setIsGrouped(false);
      setIsTimelineSorted(false);

      toast({
        title: "AI Organization Complete",
        description: `Organized ${data.positions.length} notes intelligently based on timeline and relevancy.`,
      });

    } catch (error) {
      console.error('AI organization failed:', error);
      
      // Track LLM call failure
      const endTime = Date.now();
      posthog.capture('llm_call_failed', {
        model: 'gpt-4.1-2025-04-14',
        provider: 'openai',
        input_length: reactFlowNodes.filter(n => n.data?.content).length,
        start_time: startTime,
        end_time: endTime,
        response_time: endTime - startTime,
        type: 'organize_notes',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      
      toast({
        title: "AI Organization Failed",
        description: error instanceof Error ? error.message : "Failed to organize notes with AI. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAiOrganizing(false);
    }
  };

  useKeyboardShortcuts({
    onAddNote: handleAddNote,
    onDeleteNote: () => {},
    onAnalyze: handleAnalyze,
    onQuickNote: handleQuickNote,
  });

  const handleOpenSharing = () => {
    setIsSharingPanelOpen(true);
  };

  const handleCloseSharing = () => {
    setIsSharingPanelOpen(false);
  };

  if (loading) {
    console.log('Showing loading state');
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-lg">Loading workspace...</div>
        </div>
      </div>
    );
  }

  if (error) {
    console.log('Showing error state:', error);
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-red-600 mb-4">Error loading workspace</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  console.log('Rendering NoteSpaceContent with nodes:', reactFlowNodes.length, 'edges:', reactFlowEdges.length);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Navbar
        sessionName={sessionName}
        onSessionNameChange={userPermission !== 'view' ? updateSessionName : undefined}
        onOpenSharing={handleOpenSharing}
        showSharing={true}
        userPermission={userPermission}
      />
      
      <div className={`flex flex-grow relative overflow-hidden ${isMobile ? 'note-space-content' : ''}`}>
        <ReactFlowProvider>
          {/* Prominent Mode Toggle */}
          <ModeToggle 
            mode={currentMode}
            onModeChange={handleModeChange}
          />
          
          <Toolbar 
            onAddNote={userPermission !== 'view' ? handleAddNote : () => {}}
            onAnalyze={handleAnalyze}
            isAnalyzing={isAnalyzing}
            onOpenGuidance={handleOpenGuidance}
            onOpenHighlights={handleOpenHighlights}
            mode={currentMode}
            onModeChange={handleModeChange}
            aiAnalysisEnabled={aiAnalysisEnabled}
            liveGuidanceEnabled={liveGuidanceEnabled}
            onToggleAiAnalysis={setAiAnalysisEnabled}
            onToggleLiveGuidance={undefined} // Not user-controllable, PostHog controlled
            reactFlowInstance={reactFlowInstance}
            onGroupByType={handleGroupByType}
            onUngroup={handleUngroup}
            isGrouped={isGrouped}
            noteTypeCounts={noteTypeCounts}
            onTimelineToggle={handleTimelineToggle}
            isTimelineSorted={isTimelineSorted}
            onSortChange={handleSortByTimeline}
            onAiOrganize={handleAiOrganize}
            isAiOrganizing={isAiOrganizing}
          />
          
          <div className="react-flow-wrapper flex-1 relative overflow-hidden">
            <ReactFlow
              nodes={reactFlowNodes}
              edges={reactFlowEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeDragStop={onNodeDragStop}
              onInit={(instance: ReactFlowInstance) => setReactFlowInstance(instance)}
              onDrop={userPermission !== 'view' ? onDrop : undefined}
              onDragOver={userPermission !== 'view' ? onDragOver : undefined}
              nodeTypes={nodeTypes}
              fitView
              className="bg-gray-50"
              nodesDraggable={userPermission !== 'view'}
              nodesConnectable={userPermission !== 'view'}
              elementsSelectable={true}
              panOnScroll={false}
              zoomOnScroll={true}
              zoomOnPinch={isMobile}
              panOnDrag={true}
              minZoom={0.1}
              maxZoom={2}
            >
              {!isMobile && <MiniMap />}
              <Controls showZoom={true} showFitView={true} showInteractive={true} />
              <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
            </ReactFlow>
          </div>
        </ReactFlowProvider>

        {/* Desktop panels */}
        {!isMobile && (
          <>
            <AnalysisPanel 
              isOpen={isAnalysisPanelOpen && aiAnalysisEnabled}
              onClose={handleCloseAnalysisPanel}
              isAnalyzing={isAnalyzing}
              results={analysisResults}
            />
            
            <GuidancePanel
              questions={questions.map(q => q.text)}
              notes={reactFlowNodes.map(node => {
                const noteData = getStickyNoteData(node.data);
                return { 
                  type: noteData.type, 
                  content: noteData.content 
                };
              })}
              isOpen={isGuidancePanelOpen}
              onClose={handleCloseGuidance}
              noteSpaceId={spaceId}
            />
            
            <HighlightsPanel
              isOpen={isHighlightsPanelOpen}
              onClose={handleCloseHighlights}
              currentSpaceId={spaceId}
            />
            
            <SharingPanel
              spaceId={spaceId}
              spaceName={sessionName}
              isOpen={isSharingPanelOpen}
              onClose={handleCloseSharing}
              isOwner={isOwner}
            />
          </>
        )}

        {/* Mobile drawer panels */}
        {isMobile && (
          <>
            <MobileAnalysisPanel 
              isOpen={isAnalysisPanelOpen && aiAnalysisEnabled}
              onClose={handleCloseAnalysisPanel}
              isAnalyzing={isAnalyzing}
              results={analysisResults}
            />
            
            <MobileGuidancePanel
              questions={questions.map(q => q.text)}
              notes={reactFlowNodes.map(node => {
                const noteData = getStickyNoteData(node.data);
                return { 
                  type: noteData.type, 
                  content: noteData.content 
                };
              })}
              isOpen={isGuidancePanelOpen}
              onClose={handleCloseGuidance}
              noteSpaceId={spaceId}
            />

            <MobileHighlightsPanel
              isOpen={isHighlightsPanelOpen}
              onClose={handleCloseHighlights}
              currentSpaceId={spaceId}
            />
            
            <MobileSharingPanel
              spaceId={spaceId}
              spaceName={sessionName}
              isOpen={isSharingPanelOpen}
              onClose={handleCloseSharing}
              isOwner={isOwner}
            />
          </>
        )}
        
        {showQuestionSetup && (
          <QuestionSetup 
            onComplete={handleQuestionSetupComplete} 
            spaceId={spaceId}
            saveQuestions={saveQuestions}
          />
        )}
      </div>
    </div>
  );
};

export default NoteSpace;
